import React, { useEffect, useState } from "react";
import { PageHeader, KPICard, Card, Badge, Button, Spinner } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { fmt, stageColors, stageLabels } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { TrendingUp, Briefcase, Activity, Target, ArrowUpRight, Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [deals, setDeals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, d, p] = await Promise.all([
          endpoints.dashboard(), endpoints.deals(), endpoints.projects(),
        ]);
        setData(s.data);
        setDeals(d.data);
        setProjects(p.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) {
    return <div className="p-6 md:p-10 text-white/50"><Spinner /> Loading…</div>;
  }

  const chartData = Object.entries(data.pipeline_by_stage).map(([k, v]) => ({
    stage: stageLabels[k] || k, value: Math.round(v.value / 1e6), count: v.count,
  }));

  return (
    <div className="p-4 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="Command Center"
        title={<>Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}, <span className="italic text-gold">{user?.name?.split(" ")[0] || "Operator"}</span>.</>}
        description="Real-time institutional overview of deal pipeline, risk exposure and active execution programs."
        testid="dashboard-page"
      />

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-6 md:mb-10">
        <KPICard testid="kpi-pipeline-aum" label="Pipeline AUM" value={fmt.money(data.pipeline_value)} sub={`weighted · ${fmt.money(data.weighted_pipeline)}`} accent="#C5A059" />
        <KPICard testid="kpi-active-deals" label="Active Deals" value={data.active_deals} sub={`of ${data.total_deals} total`} />
        <KPICard testid="kpi-companies" label="Target Universe" value={data.companies} sub={`${data.valuations} valuations done`} />
        <KPICard testid="kpi-risk" label="Portfolio Risk" value={data.portfolio_risk_score ? `${Math.round(data.portfolio_risk_score)}` : "—"} sub="composite index (0-100)" accent={data.portfolio_risk_score > 60 ? "#f87171" : "#34d399"} />
      </div>

      {/* Pipeline Chart + Deal list */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="overline">Deal Pipeline</div>
              <div className="font-display text-xl mt-1">By Stage · € Millions</div>
            </div>
            <Link to="/deals" className="text-[12px] text-white/50 hover:text-[#C5A059] flex items-center gap-1">
              View all <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="h-[260px] w-full" data-testid="chart-pipeline" style={{ minHeight: 260 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={260}>
              <BarChart data={chartData} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="stage" tick={{ fill: "#71717a", fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "#71717a", fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#18181b", border: "1px solid rgba(197,160,89,0.3)", borderRadius: "6px", fontSize: "12px" }}
                  cursor={{ fill: "rgba(197,160,89,0.08)" }}
                  formatter={(v) => [`€${v}M`, "Value"]}
                />
                <Bar dataKey="value" fill="#C5A059" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Hot Deals</div>
              <div className="font-display text-lg mt-1">Priority</div>
            </div>
          </div>
          <div className="space-y-3">
            {deals.slice(0, 5).map((d) => {
              const c = stageColors[d.stage] || stageColors.sourcing;
              return (
                <div key={d.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0" data-testid={`deal-row-${d.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium truncate">{d.company_name}</div>
                    <div className="text-[11px] text-white/40">{fmt.money(d.value)} · {d.probability}%</div>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded tracking-wide uppercase" style={{ background: c.bg, color: c.text }}>
                    {stageLabels[d.stage]}
                  </span>
                </div>
              );
            })}
            {!deals.length && <div className="text-white/30 text-sm py-8 text-center">No deals yet.</div>}
          </div>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { to: "/origin", icon: Target, title: "Scout Targets", desc: "Find new deals" },
          { to: "/valuations", icon: Calculator, title: "Generate Dossier", desc: "40-page M&A" },
          { to: "/risk", icon: Activity, title: "Run Risk", desc: "Monte Carlo" },
          { to: "/ask", icon: TrendingUp, title: "Ask CLARA", desc: "AI Assistant" },
        ].map((a) => (
          <Link key={a.to} to={a.to} data-testid={`quick-${a.title.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="clara-card p-4 md:p-5 h-full hover:border-[#C5A059]/40 transition group">
              <a.icon size={18} className="text-gold mb-3" />
              <div className="font-display text-base mb-0.5">{a.title}</div>
              <div className="text-[11px] text-white/40">{a.desc}</div>
              <ArrowUpRight size={14} className="mt-3 text-white/20 group-hover:text-[#C5A059] transition" />
            </div>
          </Link>
        ))}
      </div>

      {/* Projects strip */}
      <Card>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="overline">Active Execution</div>
            <div className="font-display text-xl mt-1">Programs & PMI</div>
          </div>
          <Link to="/projects" className="text-[12px] text-white/50 hover:text-[#C5A059] flex items-center gap-1">
            All programs <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {projects.slice(0, 3).map((p) => (
            <div key={p.id} className="clara-surface-2 p-4 rounded-md" data-testid={`project-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="text-[13px] font-medium flex-1 pr-2">{p.name}</div>
                <Badge tone="navy">{p.methodology}</Badge>
              </div>
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${p.progress}%`, background: "linear-gradient(90deg, #C5A059, #D4AF37)" }} />
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-white/40">
                <span>{p.progress}%</span>
                <span>{p.owner}</span>
              </div>
            </div>
          ))}
          {!projects.length && <div className="text-white/30 text-sm col-span-3 text-center py-6">No active programs.</div>}
        </div>
      </Card>
    </div>
  );
}
